import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { selectors } from "reducers";
import {
  HasPermission,
  AppPermissionContext
} from "components/shared/app-permission";
import OrdersTab from "./orders";
import ContactsTab from "./contacts";
import ChangeLogsTab from "./change-logs";
import AccessLogsTab from "./access-logs";
import EquipmentTab from "./equipment";
import WalkInOrdersTab from "./walk_in_orders";
import HcpcHistory from "./hcpc-history";
import PatientCmns from "./cmns";
import PatientDocuments from "./patient-documents";
import PatientTasks from "./tasks";
import TrackingInfoTab from "./tracking-info";
import SpecialFieldsTab from "./special-fields";
import ComplianceData from "./compliance-data/index";
import OnboardingData from "./onboarding-data/index";
import FormsData from "./forms/FormsData";
import MaskFitting from "./equipment/mask-fitting";
import PatientAppointments from "./appointments/index";
import TherapyData from "./therapy-data/index";

const Tabs = [
  { name: "Orders", permission: "orderSupplies" },
  { name: "Walk-in-Orders", permission: "orderSupplies" },
  { name: "Contacts" },
  { name: "Change Logs" },
  { name: "Access Logs" },
  { name: "Equipment" },
  { name: "Mask Fitting", permission: "maskFitting" },
  { name: "HCPCS History" },
  { name: "CMNs", permission: "orderSupplies" },
  { name: "Documents" },
  { name: "Tasks", permission: "patientTasks" },
  { name: "Tracking" },
  { name: "Special Fields" },
  { name: "Forms Data" },
  { name: "Compliance Data" },
  { name: "Onboarding Data" },
  { name: "Appointments" },
  // { name: "Therapy Data", permission: "bTherapyData" }, DEV: ver como queda el nombre del permission
  { name: "Therapy Data" },

];

function PatientTabs({
  patientId,
  readOnly,
  walkInOrders,
  patientInfo,
  defaultSelectedTab,
  displayComplianceDataSpinner,
  isViewingDashboard,
  setIsCompliant,
  reloadComplianceTab
}) {
  const [selectedTab, setSelectedTab] = useState("Orders");

  const appPermisions = useContext(AppPermissionContext);

  React.useEffect(() => {
    selectDefaultTab();
  }, [defaultSelectedTab, Tabs]);

  const selectDefaultTab = () => {
    if (
      defaultSelectedTab &&
      Tabs.find(tab => tab.name === defaultSelectedTab)
    ) {
      setSelectedTab(defaultSelectedTab);
    }
  };

  return (
    <div className="patient-tabs">
      <ul>
        {Tabs.filter(x => {
          if (x.name == "Equipment")
            return patientInfo?.CompanyLoadItemsFromCurrentEquipment;
          if (x.name == "Compliance Data" || x.name == "Forms Data")
            return (
              patientInfo?.CompanyManagesCompliance ||
              patientInfo?.patientTypeData?.compliance_patient
            );
          if (x.name == "Onboarding Data")
            return (
              patientInfo?.CompanyManagesOnboarding
            );
          if (x.name == "Therapy Data")
            return (
              appPermisions?.therapy_data
            );
          return true;
        }).map(({ name, permission }) =>
          permission ? (
            <HasPermission
              permission={permission}
              allowAdmins={false}
              key={name}
            >
              <li
                className={selectedTab == name ? "active" : ""}
                onClick={() => setSelectedTab(name)}
              >
                {name}
              </li>
            </HasPermission>
          ) : (
            <li
              className={selectedTab === name ? "active" : ""}
              onClick={() => setSelectedTab(name)}
              key={name}
            >
              {name}
            </li>
          )
        )}
      </ul>
      <div className="content">
        {selectedTab === "Orders" && <OrdersTab patientId={patientId} />}
        {selectedTab === "Contacts" && <ContactsTab patientId={patientId} />}
        {selectedTab === "Change Logs" && (
          <ChangeLogsTab patientId={patientId} patientInfo={patientInfo} />
        )}
        {selectedTab === "Access Logs" && (
          <AccessLogsTab patientId={patientId} />
        )}
        {selectedTab === "Equipment" && (
          <EquipmentTab patientId={patientId} readOnly={readOnly} />
        )}
        {selectedTab === "Walk-in-Orders" && (
          <div>
            <WalkInOrdersTab walkInOrders={walkInOrders} />
          </div>
        )}
        {selectedTab === "Mask Fitting" && (
          <MaskFitting patientId={patientId} />
        )}
        {selectedTab === "HCPCS History" && (
          <div>
            <HcpcHistory
              patientId={patientId}
              patient_product_lines={patientInfo?.patient_product_lines ?? []}
            />
          </div>
        )}
        {selectedTab === "CMNs" && (
          <PatientCmns patientId={patientId} readOnly={readOnly} />
        )}
        {selectedTab === "Documents" && (
          <div>
            <PatientDocuments patientId={patientId} readOnly={readOnly} />
          </div>
        )}
        {selectedTab === "Tasks" && (
          <PatientTasks
            patientId={patientId}
            readOnly={readOnly}
            patientInfo={patientInfo}
          />
        )}
        {selectedTab === "Tracking" && (
          <TrackingInfoTab patientId={patientId} readOnly={readOnly} />
        )}
        {selectedTab === "Special Fields" && (
          <SpecialFieldsTab patientId={patientId} readOnly={readOnly} />
        )}
        {selectedTab === "Compliance Data" && (
          <ComplianceData
            patientId={patientId}
            patientInfo={patientInfo}
            complianceActive={patientInfo?.patientTypeData?.compliance_status}
            displaySpinner={displayComplianceDataSpinner}
            setIsCompliantForBanner={value => setIsCompliant(value)}
            companyManagesCompliance={patientInfo?.CompanyManagesCompliance}
            CompanyManagesResupply={patientInfo?.CompanyManagesResupply}
            reloadComplianceTab={reloadComplianceTab}
          />
        )}
        {selectedTab === "Onboarding Data" && (
          <OnboardingData
            patientId={patientId}
            patientInfo={patientInfo}
            companyManagesOnboarding={patientInfo?.CompanyManagesOnboarding}
            companyManagesResupply={patientInfo?.CompanyManagesResupply}
            companyManagesCompliance={patientInfo?.CompanyManagesCompliance}
          />
        )}
        {selectedTab === "Forms Data" && (
          <FormsData
            patientId={patientId}
            patientTypeData={patientInfo.patientTypeData}
            isInsideModal={isViewingDashboard}
            patientInfo={patientInfo}
          />
        )}
        {selectedTab === "Appointments" && (
          <PatientAppointments patientId={patientId} onboardingAppointmentType={false} coachFilter="All" />
        )}
        {selectedTab === "Therapy Data" && (
          <TherapyData patientId={patientId} />
        )}
      </div>
    </div>
  );
}

export default connect((state, { patientId }) => ({
  walkInOrders: selectors.getPatientWalkinOrders(state, patientId)
}))(PatientTabs);

PatientTabs.propTypes = {
  patientId: PropTypes.string.isRequired,
  walkInOrders: PropTypes.arrayOf(PropTypes.object).isRequired,
  readOnly: PropTypes.bool,
  patientInfo: PropTypes.object,
  defaultSelectedTab: PropTypes.string,
  displayComplianceDataSpinner: PropTypes.bool,
  isViewingDashboard: PropTypes.func,
  setIsCompliant: PropTypes.func,
  reloadComplianceTab: PropTypes.bool
};

PatientTabs.defaultProps = {
  readOnly: false
};
